import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import styled from 'styled-components';
import * as yup from 'yup';

import AdminFormPage from 'components/admin/shared/AdminFormPage';
import Button from 'components/shared/Button';
import ButtonGroup from 'components/shared/ButtonGroup';
import CircularProgress from 'components/shared/CircularProgress';
import Foldout from 'components/shared/Foldout';
import Checkbox from 'components/shared/form/Checkbox';
import { FormContainer as Form } from 'components/shared/form/Form';
import FormHeader from 'components/shared/form/FormHeader';
import FormSection from 'components/shared/form/FormSection';
import { MultiSelectChips, Select } from 'components/shared/form/InfiniteScrollDropdown';
import Input from 'components/shared/form/Input';
import InputGroup from 'components/shared/form/InputGroup';
import SectionHeader from 'components/shared/form/SectionHeader';
import Launcher from 'components/shared/Launcher';
import { useProfile } from 'context/profile';
import { getFieldError, useAdminForm } from 'hooks/datasource';
import useInfiniteOptions from 'hooks/useInfiniteOptions';
import type { AIPrompt } from 'models/flexible/AIPrompt';
import { ClientType, IMPORT_TYPES, ImportType, MFA_FACTORS } from 'models/flexible/Client';
import type { Group } from 'models/flexible/Group';
import { PERMISSIONS } from 'models/flexible/Profile';
import { adminIndexAIPrompts } from 'services/api/admin/aiPrompts';
import { adminIndexClassifications } from 'services/api/admin/classifications';
import {
  adminClientsQueryKeys,
  adminCreateClient,
  adminIndexClients,
  adminShowClient,
  adminUpdateClient,
} from 'services/api/admin/clients';
import { indexGroupTypes } from 'services/api/admin/groupTypes';
import type { AdminAIPromptIndexFields } from 'services/api/fields';
import { colors } from 'styles/theme/colors';

import AssociatedGroupsSlideout from './AssociatedGroupsSlideout';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type NetworkGroupEntry = {
  groupTypeId: string;
  groupTypeApiName: string;
  groupTypeDisplayName: string;
  groups: Group[];
};

// ---------------------------------------------------------------------------
// Constants + helpers
// ---------------------------------------------------------------------------

const getId = (o: any) => o!.id as string;
const getName = (o: any) => o!.name as string;
const getDisplayName = (o: any) => o!.displayName as string;

type ClientTypeOption = { name: string; kind: string };
const CLIENT_TYPES: ClientTypeOption[] = Object.values(ClientType).map((v) => ({ name: v, kind: v }));

const clientSchema = yup.object({
  name: yup.string().required('Client name required'),
  clientType: yup.mixed().test('clientType', 'Client type is required', (v: any) => !!v),
  fileRetentionDays: yup
    .number()
    .nullable()
    .transform((v) => (v === '' || v === undefined ? null : v))
    .test('min', 'Must be greater than 0', (v) => v == null || v > 0)
    .test('max', 'Must be less than 2555 days (7 years)', (v) => v == null || v < 2555),
});

const getClientTypeName = (opt: { name: string } | null) => {
  if (!opt) return '';
  return opt.name === ClientType.PAYOR ? 'Payer' : opt.name;
};

const mfaIndependentOptions = Object.entries(MFA_FACTORS)
  .filter(([, v]) => v.independent)
  .map(([k, v]) => ({ id: k, name: v.label }));

const mfaDependentOptions = Object.entries(MFA_FACTORS)
  .filter(([, v]) => !v.independent)
  .map(([k, v]) => ({ id: k, name: v.label }));

const filterFactorsByIndependence = (independent: boolean, factors: string[]) =>
  factors
    .filter((f) => MFA_FACTORS[f]?.independent === independent)
    .map((f) => ({ id: f, name: MFA_FACTORS[f].label }));

const defaultMfa = () => ({
  enabled: false,
  allowedIndependentFactors: [] as { id: string; name: string }[],
  allowedDependentFactors: [] as { id: string; name: string }[],
  default: null as { id: string; name: string } | null,
});

const formatParentLabel = (c: { name: string; clientType: string } | null) => {
  if (!c) return '';
  const type = c.clientType === ClientType.PAYOR ? 'Payer' : c.clientType;
  return `${c.name} (${type})`;
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export default function EditClient() {
  const { id } = useParams<{ id: string }>();
  const { profile } = useProfile();
  const isEditing = !!id;
  const canEdit = profile.has(PERMISSIONS.adminClientEdit);
  const disabled = !canEdit;

  // --- Data + form ---

  const { values, errors, valid, setValue, submit, loading, goBack } = useAdminForm({
    id,
    show: adminShowClient,
    create: adminCreateClient,
    update: adminUpdateClient,
    include: [
      'parent',
      'children',
      'episodeClassifications',
      'planTypeClassifications',
      'groupTypes',
      'managedProviderTypes',
      'providedProviderTypes',
      'networkGroups',
      'summarizationPromptIds',
      'summarizationPrompts',
    ] as const,
    queryKey: adminClientsQueryKeys,
    schema: clientSchema,
    onCreateSuccess: { toast: 'Client successfully created!', navigate: '/admin/clients' },
    onUpdateSuccess: { toast: 'Client successfully updated!', navigate: '/admin/clients' },
    onError: { toast: 'Something went wrong.' },
    toFormValues: (data) => {
      const mfa = data?.mfaConfig ?? { enabled: false, allowedFactors: [] as string[], default: null as string | null };
      const factors = mfa.allowedFactors ?? [];

      const networkGroups: Record<string, NetworkGroupEntry> = {};
      for (const group of data?.networkGroups ?? []) {
        const gtId = group.groupType?.id;
        if (!gtId) continue;
        if (!networkGroups[gtId]) {
          networkGroups[gtId] = {
            groupTypeId: gtId,
            groupTypeApiName: group.groupType?.apiName ?? '',
            groupTypeDisplayName: group.groupType?.displayName ?? '',
            groups: [],
          };
        }
        networkGroups[gtId].groups.push(group);
      }

      return {
        id: data?.id ?? '',
        name: data?.name ?? '',
        clientType: data?.clientType ? { name: data.clientType, kind: data.clientType } : null,
        parent: data?.parent ?? null,
        children: data?.children ?? [],
        ssoEnabled: data?.ssoEnabled ?? false,
        importType: data?.importType
          ? (IMPORT_TYPES.find((t) => t.type === data.importType) ?? null)
          : (IMPORT_TYPES.find((t) => t.type === ImportType.IMPORTED_PATIENT) ?? null),
        fileRetentionDays: data?.fileRetentionDays ?? null,
        managedProviderTypes: data?.managedProviderTypes ?? [],
        providedProviderTypes: data?.providedProviderTypes ?? [],
        mfaConfig: {
          enabled: mfa.enabled,
          allowedIndependentFactors: filterFactorsByIndependence(true, factors),
          allowedDependentFactors: filterFactorsByIndependence(false, factors),
          default: mfa.default ? { id: mfa.default, name: MFA_FACTORS[mfa.default]?.label ?? mfa.default } : null,
        },
        episodeClassifications: data?.episodeClassifications ?? [],
        planTypeClassifications: data?.planTypeClassifications ?? [],
        summarizationPrompts: data?.summarizationPrompts ?? [],
        networkGroups,
      };
    },
    toPayload: (v) => ({
      name: v.name,
      clientType: v.clientType?.name,
      parentId: v.parent?.id ?? null,
      episodeClassificationIds: v.episodeClassifications.map((e) => e.id),
      planTypeClassificationIds: v.planTypeClassifications.map((p) => p.id),
      providedProviderTypeIds: v.providedProviderTypes.map((g) => g.id),
      managedProviderTypeIds: v.managedProviderTypes.map((g) => g.id),
      mfaConfig: {
        enabled: v.mfaConfig?.enabled ?? false,
        default: v.mfaConfig?.default?.id ?? null,
        allowedFactors: [...v.mfaConfig.allowedIndependentFactors, ...v.mfaConfig.allowedDependentFactors].map(
          (f) => f.id
        ),
      },
      ssoEnabled: v.ssoEnabled,
      fileRetentionDays: v.fileRetentionDays,
      networkGroupIds: Object.values(v.networkGroups).flatMap((entry) => entry.groups.map((g) => g.id)),
      importType: v.importType?.type ?? null,
      summarizationPromptIds: v.summarizationPrompts.map((p) => p.id),
    }),
  });

  // --- Dropdown options ---

  const episodeOptions = useInfiniteOptions({
    queryKey: ['adminClassifications', 'episode'],
    fetchFn: adminIndexClassifications,
    params: { type: 'episode', sortBy: 'name asc' },
  });

  const planTypeOptions = useInfiniteOptions({
    queryKey: ['adminClassifications', 'plan_type'],
    fetchFn: adminIndexClassifications,
    params: { type: 'plan_type', sortBy: 'name asc' },
  });

  const providerTypeOptions = useInfiniteOptions({
    queryKey: ['adminGroupTypes', 'provider'],
    fetchFn: indexGroupTypes,
    params: { type: 'provider', sortBy: 'displayName asc' },
  });

  const clientSelectOptions = useInfiniteOptions({
    queryKey: ['adminClients', 'select'],
    fetchFn: adminIndexClients,
    params: { sortBy: 'name asc', ...(id ? { 'id.not': id } : {}) },
  });

  const workspacePromptOptions = useInfiniteOptions<AIPrompt<AdminAIPromptIndexFields>>({
    queryKey: ['adminAIPrompts', 'summarization'],
    fetchFn: adminIndexAIPrompts,
    params: { targetClassname: 'Summarization', isPublished: true, sortBy: 'name asc' },
  });

  const groupTypeOptions = useInfiniteOptions({
    queryKey: ['adminGroupTypes', 'all'],
    fetchFn: indexGroupTypes,
    params: { sortBy: 'displayName asc' },
  });

  // --- Local state ---

  const [slideout, setSlideout] = useState<NetworkGroupEntry | null>(null);
  const networkGroupsMap: Record<string, NetworkGroupEntry> = values?.networkGroups ?? {};

  const sortedNetworkGroups = Object.values(networkGroupsMap).sort((a, b) =>
    a.groupTypeDisplayName.localeCompare(b.groupTypeDisplayName)
  );

  const enabledGroupTypeIds = new Set([
    ...(values?.managedProviderTypes ?? []).map((g) => g.id),
    ...(values?.providedProviderTypes ?? []).map((g) => g.id),
  ]);

  // --- Render ---

  if (isEditing && loading) {
    return <CircularProgress style={{ margin: '48px auto', display: 'block' }} />;
  }

  return (
    <AdminFormPage>
      <FormHeader title={isEditing ? 'Edit Client' : 'Add Client'} />
      <Form>
        <StyledFormSection>
          <SectionHeader>Organization Setup</SectionHeader>

          <InputGroup title='Client Name'>
            <Input
              value={values?.name ?? ''}
              onChange={(e) => setValue('name', e.target.value)}
              placeholder='Client Name'
              disabled={disabled}
            />
            {errors?.name && <ErrorText>{errors.name}</ErrorText>}
          </InputGroup>

          <InputGroup title='Client Type'>
            <Select
              options={CLIENT_TYPES}
              value={values?.clientType ?? null}
              onChange={(v) => {
                setValue('clientType', v);
                setValue('networkGroups', {});
              }}
              getOptionValue={(o: ClientTypeOption) => o.kind}
              getOptionLabel={getClientTypeName}
              placeholder='Client Type'
              isDisabled={disabled || !!values?.children?.length}
            />
            {!!getFieldError(errors, 'clientType') && <ErrorText>{getFieldError(errors, 'clientType')}</ErrorText>}
          </InputGroup>

          <InputGroup title='Parent Client (optional)'>
            <Select
              {...clientSelectOptions}
              value={values?.parent ?? null}
              onChange={(v) => setValue('parent', v)}
              getOptionValue={getId}
              getOptionLabel={formatParentLabel}
              placeholder='Parent Client'
              isClearable
              isDisabled={disabled}
            />
          </InputGroup>

          {!!values?.children?.length && (
            <InputGroup>
              <MultiSelectChips
                title='Associated Clients (optional)'
                {...clientSelectOptions}
                values={values.children}
                onChange={(v) => setValue('children', v)}
                getOptionValue={getId}
                getOptionLabel={getName}
                disabled={disabled}
              />
            </InputGroup>
          )}

          <CheckboxRow>
            <Checkbox
              checked={values?.ssoEnabled ?? false}
              onChange={(val) => {
                setValue('ssoEnabled', val);
                if (val) setValue('mfaConfig', defaultMfa());
              }}
              disabled={disabled}
            />
            <label>SSO Enabled</label>
          </CheckboxRow>

          <InputGroup title='Import Type'>
            <Select
              options={IMPORT_TYPES}
              value={values?.importType ?? null}
              onChange={(v) => setValue('importType', v)}
              getOptionValue={(o) => o!.type}
              getOptionLabel={(o) => o!.label}
              placeholder='Import Type'
              isClearable
              isDisabled={disabled}
            />
          </InputGroup>

          <InputGroup title='File Retention Days (optional)'>
            <Input
              value={values?.fileRetentionDays ?? ''}
              onChange={(e) => setValue('fileRetentionDays', e.target.value ? Number(e.target.value) : null)}
              placeholder='File Retention Days'
              type='number'
              disabled={disabled}
            />
            {!!getFieldError(errors, 'fileRetentionDays') && (
              <ErrorText>{getFieldError(errors, 'fileRetentionDays')}</ErrorText>
            )}
          </InputGroup>

          <SectionHeader>Enabled Care Options</SectionHeader>
          <DescriptionText>Select which care options this client will use to manage patients</DescriptionText>
          <InputGroup>
            <MultiSelectChips
              title='Managed Care Options (optional)'
              {...providerTypeOptions}
              values={values?.managedProviderTypes ?? []}
              onChange={(v) => setValue('managedProviderTypes', v)}
              getOptionValue={getId}
              getOptionLabel={getDisplayName}
              placeholder='Managed Care Options'
              disabled={disabled}
            />
          </InputGroup>
          <DescriptionText>Select which care options this client will use to provide patient updates</DescriptionText>
          <InputGroup>
            <MultiSelectChips
              title='Provided Care Options (optional)'
              {...providerTypeOptions}
              values={values?.providedProviderTypes ?? []}
              onChange={(v) => setValue('providedProviderTypes', v)}
              getOptionValue={getId}
              getOptionLabel={getDisplayName}
              placeholder='Provided Care Options'
              disabled={disabled}
            />
          </InputGroup>

          <SectionHeader>Multi-factor Authentication (MFA) Configuration</SectionHeader>
          <CheckboxRow>
            <Checkbox
              checked={values?.mfaConfig?.enabled ?? false}
              onChange={(val) => {
                if (val) {
                  setValue('mfaConfig', { ...values.mfaConfig, enabled: true });
                } else {
                  setValue('mfaConfig', defaultMfa());
                }
              }}
              disabled={disabled || values?.ssoEnabled}
            />
            <label>MFA Enabled</label>
          </CheckboxRow>

          {values?.mfaConfig?.enabled && (
            <>
              <InputGroup>
                <MultiSelectChips
                  title='Enabled Independent Factors'
                  options={mfaIndependentOptions}
                  values={values.mfaConfig.allowedIndependentFactors ?? []}
                  onChange={(factors) => {
                    const defaultStillValid = factors.some((f) => f.id === values.mfaConfig?.default?.id);
                    setValue('mfaConfig', {
                      ...values.mfaConfig,
                      allowedIndependentFactors: factors,
                      default: defaultStillValid ? values.mfaConfig.default : null,
                    });
                  }}
                  getOptionValue={getId}
                  getOptionLabel={getName}
                  placeholder='Enabled Independent Factors'
                  disabled={disabled || values.ssoEnabled}
                />
              </InputGroup>
              <InputGroup title='Default Factor'>
                <Select
                  options={values.mfaConfig.allowedIndependentFactors ?? []}
                  value={values.mfaConfig.default ?? null}
                  onChange={(v) => setValue('mfaConfig', { ...values.mfaConfig, default: v })}
                  getOptionValue={getId}
                  getOptionLabel={getName}
                  placeholder='Default Factor'
                  isDisabled={disabled || values.ssoEnabled}
                />
              </InputGroup>
              <InputGroup>
                <MultiSelectChips
                  title='Enabled Dependent Factors (optional)'
                  options={mfaDependentOptions}
                  values={values.mfaConfig.allowedDependentFactors ?? []}
                  onChange={(v) => setValue('mfaConfig', { ...values.mfaConfig, allowedDependentFactors: v })}
                  getOptionValue={getId}
                  getOptionLabel={getName}
                  placeholder='Enabled Dependent Factors'
                  disabled={disabled || values.ssoEnabled}
                />
              </InputGroup>
            </>
          )}

          <SectionHeader>Episodes and Plan Types</SectionHeader>
          <InputGroup>
            <MultiSelectChips
              title='Episode Types'
              {...episodeOptions}
              values={values?.episodeClassifications ?? []}
              onChange={(v) => setValue('episodeClassifications', v)}
              getOptionValue={getId}
              getOptionLabel={getName}
              placeholder='Episodes'
              disabled={disabled}
            />
          </InputGroup>
          <InputGroup>
            <MultiSelectChips
              title='Plan Types'
              {...planTypeOptions}
              values={values?.planTypeClassifications ?? []}
              onChange={(v) => setValue('planTypeClassifications', v)}
              getOptionValue={getId}
              getOptionLabel={getName}
              placeholder='Plan Types'
              disabled={disabled}
            />
          </InputGroup>

          <SectionHeader>Workspace Prompts</SectionHeader>
          <InputGroup>
            <MultiSelectChips
              title='Workspace Prompts'
              {...workspacePromptOptions}
              values={values?.summarizationPrompts ?? []}
              onChange={(v) => setValue('summarizationPrompts', v)}
              getOptionValue={getId}
              getOptionLabel={(o) => o!.name ?? o!.id}
              placeholder='Add a workspace prompt'
              disabled={disabled}
              formatOptionLabel={(o, meta) => {
                if (!o) return null;
                if (meta?.context === 'value') return o.name ?? o.id;
                return (
                  <div>
                    <div>{o.name}</div>
                    <div style={{ fontSize: '0.9em', color: colors.black50 }}>{o.displayName}</div>
                  </div>
                );
              }}
            />
          </InputGroup>

          <SectionHeader>Associated Groups</SectionHeader>
          <InputGroup title='Add a group type'>
            <Select
              {...groupTypeOptions}
              value={null}
              onChange={(gt) => {
                if (!gt) return;
                setValue('networkGroups', {
                  ...networkGroupsMap,
                  [gt.id]: { groupTypeId: gt.id, groupTypeDisplayName: gt.displayName, groups: [] },
                });
              }}
              getOptionValue={getId}
              getOptionLabel={getDisplayName}
              placeholder='Add a group type'
              isDisabled={disabled}
              filterOption={(option: any) => !networkGroupsMap[option.data.id]}
            />
          </InputGroup>

          {sortedNetworkGroups.map((entry) => (
            <Launcher
              key={entry.groupTypeId}
              label={entry.groupTypeDisplayName}
              subLabel={`${entry.groups.length} associated`}
              handleLaunch={() => setSlideout(entry)}
              pillText='Enabled'
              pillColors={{ color: '#2d550c', borderColor: '#2d550c', backgroundColor: '#f7fcf3' }}
              showPill={enabledGroupTypeIds.has(entry.groupTypeId)}
            />
          ))}
        </StyledFormSection>

        <ButtonGroup>
          <Button variant='ghost' onClick={goBack}>
            Cancel
          </Button>
          <Button onClick={submit} disabled={disabled || !valid}>
            {isEditing ? 'Update Client' : 'Create Client'}
          </Button>
        </ButtonGroup>
      </Form>

      <Foldout open={!!slideout} position='right' width='min(80%, 917px)' onBackdropClick={() => setSlideout(null)}>
        {slideout && (
          <AssociatedGroupsSlideout
            clientId={id}
            groupTypeId={slideout.groupTypeId}
            groupTypeApiName={slideout.groupTypeApiName}
            groupTypeDisplayName={slideout.groupTypeDisplayName}
            groups={slideout.groups}
            handleClose={() => setSlideout(null)}
            handleApply={(groupTypeId, groups) => {
              setValue('networkGroups', {
                ...networkGroupsMap,
                [groupTypeId]: { ...networkGroupsMap[groupTypeId], groups },
              });
              setSlideout(null);
            }}
            disabled={disabled}
          />
        )}
      </Foldout>
    </AdminFormPage>
  );
}

// ---------------------------------------------------------------------------
// Styled
// ---------------------------------------------------------------------------

const StyledFormSection = styled(FormSection)`
  margin-bottom: 40px;
`;

const CheckboxRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 8px 0 24px;
`;

const ErrorText = styled.span`
  color: ${colors.accentRed};
  font-size: 12px;
`;

const DescriptionText = styled.p`
  font-size: 14px;
  margin: 14px 0;
`;
